import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// PO Landing Forecast -- when open purchase orders are predicted to land, so
// the dispatch team can see a multi-item dump coming and staff for it.
// Prediction (get_po_landing_forecast, db_schema/po_landing_forecast.sql) is
// PO created date + configured working days per supplier and PO type, or a
// fixed date the user dictates. Suppliers with no config row are left off the
// timeline and listed under "Not configured" with an Add button.

const WORKING_DAYS = 20; // 4 weeks
const COLOURS = ['#C8922A', '#60a5fa', '#4ade80', '#a78bfa', '#f472b6', '#f87171', '#2dd4bf', '#facc15', '#fb923c', '#94a3b8'];

// ── Date helpers (all 'YYYY-MM-DD' strings, NZ calendar) ─────────────────────
const todayNz = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });
const toUtc   = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const fromUtc = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = toUtc(s); d.setUTCDate(d.getUTCDate() + n); return fromUtc(d); };
const isWeekend = (s) => { const w = toUtc(s).getUTCDay(); return w === 0 || w === 6; };
const rollToWeekday = (s) => { let d = s; while (isWeekend(d)) d = addDays(d, 1); return d; };
const workingDaysFrom = (start, n) => {
  const out = [];
  let d = rollToWeekday(start);
  while (out.length < n) { if (!isWeekend(d)) out.push(d); d = addDays(d, 1); }
  return out;
};
const fmtDay = (s) => toUtc(s).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland' }) : '';
const fmtUnits = (n) => Math.round(Number(n) || 0).toLocaleString('en-NZ');

// ── Timeline chart ───────────────────────────────────────────────────────────
const LandingChart = ({ buckets, suppliers, colourOf, selected, onSelect }) => {
  const chartW = 900, chartH = 240, padL = 44, padR = 12, padT = 12, padB = 40;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const maxVal = Math.max(1, ...buckets.map(b => b.total));
  const slot   = innerW / buckets.length;
  const barW   = Math.max(6, slot * 0.7);
  const toY    = (v) => padT + innerH - (v / maxVal) * innerH;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', minWidth: 600, height: 'auto', display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map(f => (
          <g key={f}>
            <line x1={padL} x2={chartW - padR} y1={toY(f * maxVal)} y2={toY(f * maxVal)}
              stroke="var(--border)" strokeWidth={0.5} strokeDasharray="3,3" />
            <text x={padL - 4} y={toY(f * maxVal) + 3.5} textAnchor="end" fontSize={9} fill="var(--muted)">
              {Math.round(f * maxVal)}
            </text>
          </g>
        ))}

        {buckets.map((b, i) => {
          const x = padL + i * slot + (slot - barW) / 2;
          let acc = 0;
          const isSel = selected === b.key;
          return (
            <g key={b.key} style={{ cursor: 'pointer' }} onClick={() => onSelect(isSel ? null : b.key)}>
              <rect x={padL + i * slot} y={padT} width={slot} height={innerH}
                fill={isSel ? 'rgba(255,255,255,0.06)' : 'transparent'} />
              {suppliers.map(s => {
                const v = b.bySupplier[s] || 0;
                if (!v) return null;
                const y = toY(acc + v);
                const h = toY(acc) - y;
                acc += v;
                return (
                  <rect key={s} x={x} y={y} width={barW} height={Math.max(h, 0.5)} fill={colourOf(s)}
                    opacity={b.key === 'overdue' ? 0.55 : 1}>
                    <title>{`${b.label}: ${s} — ${fmtUnits(v)} units`}</title>
                  </rect>
                );
              })}
              {b.total > 0 && (
                <text x={x + barW / 2} y={toY(b.total) - 3} textAnchor="middle" fontSize={8} fill="var(--white)">
                  {fmtUnits(b.total)}
                </text>
              )}
              <text x={x + barW / 2} y={chartH - padB + 13} textAnchor="middle" fontSize={8}
                fill={b.key === 'overdue' ? '#f87171' : 'var(--muted)'}>
                {b.short1}
              </text>
              <text x={x + barW / 2} y={chartH - padB + 24} textAnchor="middle" fontSize={8} fill="var(--muted)">
                {b.short2}
              </text>
            </g>
          );
        })}
      </svg>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
        {suppliers.map(s => (
          <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: colourOf(s), display: 'inline-block' }} />
            {s}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Config row (editable) ────────────────────────────────────────────────────
const inputStyle = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--white)', fontSize: 12, padding: '4px 8px',
};

const ConfigRow = ({ row, onSaved, onRemoved }) => {
  const [draft, setDraft]   = useState(row);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  useEffect(() => { setDraft(row); }, [row]);

  const dirty = ['supplier_label', 'mode', 'ordered_days', 'invoiced_days', 'absolute_date', 'notes']
    .some(k => String(draft[k] ?? '') !== String(row[k] ?? ''));
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setErr(null);
    if (draft.mode === 'absolute' && !draft.absolute_date) { setErr('Pick a date for Absolute'); return; }
    setSaving(true);
    const { error } = await supabase.from('po_landing_config').update({
      supplier_label: draft.supplier_label,
      mode:           draft.mode,
      ordered_days:   Number(draft.ordered_days) || 0,
      invoiced_days:  Number(draft.invoiced_days) || 0,
      absolute_date:  draft.absolute_date || null,
      notes:          draft.notes || null,
      updated_at:     new Date().toISOString(),
    }).eq('id', row.id);
    setSaving(false);
    if (error) setErr(error.message); else onSaved();
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${row.supplier_label} from the forecast? Its POs move to "Not configured".`)) return;
    const { error } = await supabase.from('po_landing_config').delete().eq('id', row.id);
    if (error) setErr(error.message); else onRemoved();
  };

  return (
    <tr className="data-row" style={{ cursor: 'default' }}>
      <td><input style={{ ...inputStyle, width: 220 }} value={draft.supplier_label || ''} onChange={e => set('supplier_label', e.target.value)} /></td>
      <td>
        <select style={inputStyle} value={draft.mode} onChange={e => set('mode', e.target.value)}>
          <option value="days">Days after PO</option>
          <option value="absolute">Absolute date</option>
        </select>
      </td>
      <td className="num">
        <input type="number" min={0} style={{ ...inputStyle, width: 60 }} disabled={draft.mode === 'absolute'}
          value={draft.ordered_days} onChange={e => set('ordered_days', e.target.value)} />
      </td>
      <td className="num">
        <input type="number" min={0} style={{ ...inputStyle, width: 60 }} disabled={draft.mode === 'absolute'}
          value={draft.invoiced_days} onChange={e => set('invoiced_days', e.target.value)} />
      </td>
      <td>
        <input type="date" style={inputStyle} disabled={draft.mode !== 'absolute'}
          value={draft.absolute_date || ''} onChange={e => set('absolute_date', e.target.value)} />
      </td>
      <td><input style={{ ...inputStyle, width: 180 }} value={draft.notes || ''} onChange={e => set('notes', e.target.value)} /></td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button className="action-btn" style={{ fontSize: 11, padding: '4px 12px' }} disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>{' '}
        <button className="action-btn action-btn--ghost" style={{ fontSize: 11, padding: '4px 12px' }} onClick={remove}>
          Remove
        </button>
        {err && <div style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>{err}</div>}
      </td>
    </tr>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const PoLanding = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [config, setConfig]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selected, setSelected] = useState(null); // bucket key or null = all
  const [adding, setAdding]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [fc, cfg] = await Promise.all([
      supabase.rpc('get_po_landing_forecast'),
      supabase.from('po_landing_config').select('*').order('supplier_label'),
    ]);
    if (fc.error || cfg.error) {
      setError((fc.error || cfg.error).message);
    } else {
      setRows(fc.data || []);
      setConfig(cfg.data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = todayNz();
  const days  = useMemo(() => workingDaysFrom(today, WORKING_DAYS), [today]);

  // Bucket key for a predicted date: 'overdue' (before today), a working day in
  // the window, or 'later'. A weekend absolute date lands on the next weekday.
  const bucketOf = useCallback((pd) => {
    if (!pd) return null;
    const d = rollToWeekday(pd);
    if (d < today) return 'overdue';
    if (d > days[days.length - 1]) return 'later';
    return d;
  }, [today, days]);

  const configured   = useMemo(() => rows.filter(r => r.configured), [rows]);
  const unconfigured = useMemo(() => {
    const m = new Map();
    rows.filter(r => !r.configured).forEach(r => {
      const cur = m.get(r.member_id) || { member_id: r.member_id, supplier: r.supplier, pos: 0, units: 0 };
      cur.pos += 1;
      cur.units += Number(r.units_open) || 0;
      m.set(r.member_id, cur);
    });
    return [...m.values()].sort((a, b) => b.units - a.units);
  }, [rows]);

  const suppliers = useMemo(() => [...new Set(configured.map(r => r.supplier))].sort(), [configured]);
  const colourOf  = useCallback((s) => COLOURS[suppliers.indexOf(s) % COLOURS.length], [suppliers]);

  const buckets = useMemo(() => {
    const list = [
      { key: 'overdue', label: 'Overdue', short1: 'Overdue', short2: '' },
      ...days.map(d => {
        const [wd, rest] = [toUtc(d).toLocaleDateString('en-NZ', { weekday: 'short', timeZone: 'UTC' }),
                            toUtc(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'numeric', timeZone: 'UTC' })];
        return { key: d, label: fmtDay(d), short1: d === today ? 'Today' : wd, short2: rest };
      }),
    ].map(b => ({ ...b, total: 0, bySupplier: {} }));
    const idx = Object.fromEntries(list.map((b, i) => [b.key, i]));
    configured.forEach(r => {
      const k = bucketOf(r.predicted_date);
      if (idx[k] == null) return;
      const b = list[idx[k]];
      const u = Number(r.units_open) || 0;
      b.total += u;
      b.bySupplier[r.supplier] = (b.bySupplier[r.supplier] || 0) + u;
    });
    return list;
  }, [days, today, configured, bucketOf]);

  const sumUnits = (pred) => configured.filter(pred).reduce((a, r) => a + (Number(r.units_open) || 0), 0);
  const overdueUnits = sumUnits(r => bucketOf(r.predicted_date) === 'overdue');
  const next5Units   = sumUnits(r => days.slice(0, 5).includes(bucketOf(r.predicted_date)));
  const windowUnits  = sumUnits(r => days.includes(bucketOf(r.predicted_date)));
  const laterUnits   = sumUnits(r => bucketOf(r.predicted_date) === 'later');

  const detailRows = selected ? configured.filter(r => bucketOf(r.predicted_date) === selected) : configured;
  const selectedLabel = selected ? buckets.find(b => b.key === selected)?.label : null;

  const addSupplier = async (u) => {
    setAdding(u.member_id);
    const { error: err } = await supabase.from('po_landing_config').insert({
      cin7_member_id: u.member_id, supplier_label: u.supplier, ordered_days: 2, invoiced_days: 2,
    });
    setAdding(null);
    if (err) setError(err.message); else load();
  };

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/dispatch-tools')}>← Dispatch Tools</button>
        <span className="page-nav__title">PO Landing Forecast</span>
        <button className="app-header__btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : '↻ Refresh'}</button>
      </div>

      {error && <div className="empty-row" style={{ color: '#f87171' }}>{error}</div>}

      {/* KPIs + chart */}
      <section className="section" style={{ padding: '20px 24px' }}>
        <p style={{ color: 'var(--muted)', fontSize: 13, margin: '0 0 16px' }}>
          Units still to land on open POs, by predicted landing day (PO date + working days set per supplier below).
          Next 4 weeks, working days only. Click a bar to list its POs.
        </p>
        <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          {[
            { label: 'Overdue (past predicted date)', value: overdueUnits, color: overdueUnits ? '#f87171' : 'var(--muted)' },
            { label: 'Next 5 working days',           value: next5Units,   color: '#C8922A' },
            { label: 'Next 4 weeks',                  value: windowUnits,  color: 'var(--white)' },
            { label: 'Later than 4 weeks',            value: laterUnits,   color: 'var(--muted)' },
          ].map(k => (
            <div key={k.label} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 16px', minWidth: 110 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: k.color, lineHeight: 1.1 }}>{fmtUnits(k.value)}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>
        {loading
          ? <div className="loading-row">Loading…</div>
          : <LandingChart buckets={buckets} suppliers={suppliers} colourOf={colourOf} selected={selected} onSelect={setSelected} />}
      </section>

      {/* PO detail */}
      <section className="section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px' }}>
          <h2 className="section-header__title" style={{ margin: 0 }}>
            {selected ? `POs landing ${selectedLabel}` : 'All forecast POs'}
          </h2>
          <span className="section-header__count">{detailRows.length}</span>
          {selected && (
            <button className="action-btn action-btn--ghost" style={{ fontSize: 11, padding: '4px 12px' }} onClick={() => setSelected(null)}>
              Show all
            </button>
          )}
        </div>
        {!loading && !detailRows.length && <div className="empty-row">No POs.</div>}
        {detailRows.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>PO</th><th>Supplier</th><th>Type</th><th>Supplier ref</th>
                  <th>Created</th><th>Predicted</th><th className="num">Units</th><th className="num">Lines</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map(r => {
                  const overdue = bucketOf(r.predicted_date) === 'overdue';
                  return (
                    <tr key={r.order_id} className="data-row" style={{ cursor: 'default' }}>
                      <td className="bold">{r.order_ref}</td>
                      <td>{r.supplier}</td>
                      <td><span className={`pill ${r.po_type === 'Invoiced' ? 'pill--green' : 'pill--blue'}`}>{r.po_type}</span></td>
                      <td className="muted">{r.supplier_invoice_ref}</td>
                      <td className="muted">{fmtDate(r.created_date)}</td>
                      <td style={{ color: overdue ? '#f87171' : undefined }}>{r.predicted_date ? fmtDay(r.predicted_date) : ''}</td>
                      <td className="num">{fmtUnits(r.units_open)}</td>
                      <td className="num">{r.lines_open}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Supplier config */}
      <section className="section">
        <div style={{ padding: '16px 24px 0' }}>
          <h2 className="section-header__title" style={{ margin: 0 }}>Supplier landing times</h2>
        </div>
        <p className="section__desc" style={{ paddingTop: 8 }}>
          Ordered = POs we placed (Smart Buyer, Auto-Restock, manual). Invoiced = POs loaded from a supplier
          invoice (supplier ref starts with INV or a number), already shipped. Days are working days after the
          PO date. Absolute date puts every open PO for that supplier on the date you pick.
        </p>
        {config.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Supplier</th><th>Mode</th><th className="num">Ordered days</th><th className="num">Invoiced days</th>
                  <th>Absolute date</th><th>Notes</th><th></th>
                </tr>
              </thead>
              <tbody>
                {config.map(c => <ConfigRow key={c.id} row={c} onSaved={load} onRemoved={load} />)}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ padding: '18px 24px 6px' }}>
          <h3 style={{ margin: 0, fontSize: 13, color: 'var(--white)' }}>Not configured</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>
            Suppliers with open POs that are left off the forecast. Add one to start it at 2 working days, then adjust above.
          </p>
        </div>
        {!loading && !unconfigured.length && <div className="empty-row">Every supplier with open POs is configured.</div>}
        {unconfigured.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Supplier</th><th className="num">Open POs</th><th className="num">Units</th><th></th></tr>
              </thead>
              <tbody>
                {unconfigured.map(u => (
                  <tr key={u.member_id} className="data-row" style={{ cursor: 'default' }}>
                    <td>{u.supplier}</td>
                    <td className="num">{u.pos}</td>
                    <td className="num">{fmtUnits(u.units)}</td>
                    <td>
                      <button className="action-btn" style={{ fontSize: 11, padding: '4px 12px' }}
                        disabled={adding === u.member_id} onClick={() => addSupplier(u)}>
                        {adding === u.member_id ? 'Adding…' : '+ Add'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default PoLanding;
